import { useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import useMerchantPortalAxios from "../../utils/axios";
import { ENTITY_TYPES } from "../Components/Sidebar/utils/sidebarStorage";
import {
  setError as setAppError,
  setFeatureData,
  setTableData,
  setTableLoading,
} from "../store/slices/appSlice";

const toUTCDateString = (date) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
    .toISOString()
    .slice(0, 10);

const getDefaultDateRange = () => {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 1);

  return {
    from: toUTCDateString(from),
    to: toUTCDateString(to),
  };
};

const currencyById = {
  1: "USD",
  2: "LBP",
  3: "EUR",
  178: "EUR",
};
const EXCHANGE_BALANCES_STORAGE_KEY = "NewExchangeBalances";

function normalizeApprovalRow(row) {
  const rawId = row?.Transaction ? String(row.Transaction) : row?.Reference;
  const currencyId = Number(row?.CurrencyID ?? row?.CurrencyId ?? 0);
  const currencyId2 = Number(row?.CurrencyID2 ?? row?.CurrencyId2 ?? 0);
  const externalTransferCurrency = row?.ExternalTransferCurrency || "";
  const externalTransferTypeItem = row?.eExternalTransferTypeItem || "";

  return {
    id: rawId || row?.PaymentDate || row?.Method,
    date: row?.PaymentDate,
    name: row?.Description || row?.Method || "Awaiting approval",
    reference: rawId,
    type: row?.Method || row?.Type || "Approval",
    method: row?.IsBankAccountTransfer ? "Bank Transfer" : row?.Method || "-",
    amount: row?.Amount,
    amount2: row?.Amount2,
    currency: row?.Currency || row?.CurrencyCode || "",
    currencyId,
    currencyId2,
    externalTransferCurrency,
    externalTransferTypeItem,
    sign: row?.Sign,
    typeId: Number(row?.TypeID ?? 0),
    status: row?.Status || "Pending",
    original: row,
  };
}

function normalizeSalesRow(row) {
  return {
    id: row?.Transaction || row?.Reference || row?.Row,
    date: row?.PaymentDate,
    name: row?.Client || row?.Description || row?.Reference || "Sales transaction",
    reference: row?.Reference || row?.Transaction,
    type: row?.Method || row?.TransactionType || "Sales",
    method: row?.Branch || row?.Till || row?.Method || "-",
    amount: row?.Amount,
    currency: currencyById[row?.CurrencyId] || row?.Currency || "",
    sign: row?.Sign,
    status: row?.Status || "Completed",
    original: row,
  };
}

function normalizeBalanceRow(row) {
  const currencyId = row?.CurrencyID || row?.CurrencyId || row?.ID;
  const balances = row?.CurrencyBalances || row;
  const explicitCurrency =
    row?.Currency ||
    row?.CurrencyCode ||
    row?.CurrencyName ||
    row?.Code;

  return {
    id: currencyId,
    accountNumber: row?.AccNumber ?? row?.AccountNumber ?? row?.WalletID ?? "",
    currency:
      currencyById[currencyId] ||
      explicitCurrency ||
      (currencyId ? `Currency ${currencyId}` : ""),
    available: balances?.Available ?? balances?.AvailableBalance ?? 0,
    book: balances?.Book ?? 0,
    held: balances?.Held ?? 0,
    connectionState: row?.State,
    lastBalanceDate: row?.LastBalanceDate,
    original: row,
  };
}

function normalizeBalanceRows(rows) {
  return (Array.isArray(rows) ? rows : []).map(normalizeBalanceRow);
}

function extractExchangeBalancesFromSections(sections = []) {
  const allBalances = (Array.isArray(sections) ? sections : []).flatMap((section) =>
    Array.isArray(section?.balances) ? section.balances : [],
  );

  const byCurrency = allBalances.reduce((acc, item) => {
    const code = String(item?.currency || "").toUpperCase();
    if (!code || acc[code]) return acc;
    acc[code] = item;
    return acc;
  }, {});

  return {
    usdBalance: Number(byCurrency?.USD?.available ?? 0),
    lbpBalance: Number(byCurrency?.LBP?.available ?? 0),
    updatedAt: new Date().toISOString(),
  };
}

export default function useNewOverviewAPI() {
  const dispatch = useDispatch();
  const client = useMerchantPortalAxios();
  const clientRef = useRef(client);
  const [transactions, setTransactions] = useState([]);
  const [balanceSections, setBalanceSections] = useState([]);
  const [bankAccountStatus, setBankAccountStatus] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [error, setError] = useState(null);

  clientRef.current = client;

  const getOverviewTransactions = useCallback(async () => {
    const entityType = sessionStorage.getItem("EntityType");
    const { from, to } = getDefaultDateRange();
    const tableKey =
      entityType === ENTITY_TYPES.merchant
        ? "salesTransactions"
        : "pendingApprovals";

    setLoading(true);
    setError(null);
    dispatch(setTableLoading({ key: tableKey, loading: true }));
    dispatch(setAppError(null));

    try {
      if (entityType === ENTITY_TYPES.merchant) {
        const response = await clientRef.current.post("/v1/Sales/GetSalesTransactions", {
          SessionID: sessionStorage.getItem("SessionID"),
          CurrencyID: 1,
          page: 1,
          pageSize: 6,
          DateFrom: from,
          DateTo: to,
          TransactionType: "All",
          BranchName: null,
          TillName: null,
          IsCustomBranch: false,
        });

        const rows = response.data.Payload?.SalesTransactions || [];
        setTransactions(rows.map(normalizeSalesRow));
        setTotalCount(rows[0]?.TotalCount || rows.length);
        dispatch(
          setTableData({
            key: tableKey,
            data: rows.map(normalizeSalesRow),
            pagination: {
              page: 1,
              totalPages: rows[0]?.TotalPages || 1,
              totalCount: rows[0]?.TotalCount || rows.length,
              pageSize: 6,
            },
          }),
        );
        return rows;
      }

      const response = await clientRef.current.post(
        "/v1/Overview/GetTransactionsToApprove",
        {
          SessionID: sessionStorage.getItem("SessionID"),
        },
      );

      const rows = response.data.Payload?.TransactionsAwaitingApproval || [];
      setTransactions(rows.map(normalizeApprovalRow));
      setTotalCount(rows.length);
      dispatch(
        setTableData({
          key: tableKey,
          data: rows.map(normalizeApprovalRow),
          pagination: {
            page: 1,
            totalPages: rows[0]?.TotalPages || 1,
            totalCount: rows.length,
            pageSize: 6,
          },
        }),
      );
      return rows;
    } catch (requestError) {
      setError(requestError);
      setTransactions([]);
      setTotalCount(0);
      dispatch(
        setTableData({
          key: tableKey,
          data: [],
          pagination: {
            page: 1,
            totalPages: 0,
            totalCount: 0,
            pageSize: 6,
          },
        }),
      );
      dispatch(
        setAppError(
          requestError?.response?.data?.Message ||
            requestError?.response?.data?.message ||
            "Unable to load overview transactions.",
        ),
      );
      throw requestError;
    } finally {
      setLoading(false);
      dispatch(setTableLoading({ key: tableKey, loading: false }));
    }
  }, [dispatch]);

  const getOverviewBalances = useCallback(async () => {
    const { from, to } = getDefaultDateRange();

    setBalancesLoading(true);

    try {
      const statusResponse = await clientRef.current.post(
        "/v1/BankAccount/GetBankAccountStatus",
        {
          SessionID: sessionStorage.getItem("SessionID"),
        },
      );
      const nextBankAccountStatus =
        statusResponse.data.Payload?.bankAccountStatus ?? null;

      setBankAccountStatus(nextBankAccountStatus);

      const suyoolResponse = await clientRef.current.post(
        "/v1/Overview/GetAccountBalance",
        {
          SessionID: sessionStorage.getItem("SessionID"),
          DateFrom: from,
          DateTo: to,
        },
      );

      const sections = [
        {
          id: "suyool",
          title: "SUYOOL",
          subtitle: "Wallet balances",
          status: "active",
          balances: normalizeBalanceRows(
            suyoolResponse.data.Payload?.CurrenciesOverview || [],
          ),
        },
      ];

      let bslBalances = [];

      try {
        const bslResponse = await clientRef.current.post(
          "/v1/Overview/GetBankAccountBalance",
          {
            SessionID: sessionStorage.getItem("SessionID"),
            DateFrom: from,
            DateTo: to,
          },
        );

        bslBalances = normalizeBalanceRows(
          bslResponse.data.Payload?.CurrenciesOverview || [],
        );
      } catch (_) {
        bslBalances = [];
      }

      if (nextBankAccountStatus === 1) {
        sections.push({
          id: "bsl",
          title: "BSL BANK",
          subtitle: "Bank account balances",
          status: "pending",
          balances: bslBalances,
        });
      }

      if (nextBankAccountStatus === 0) {
        sections.push({
          id: "bsl",
          title: "BSL BANK",
          subtitle: "Bank account balances",
          status: "apply",
          balances: bslBalances,
        });
      }

      if (nextBankAccountStatus === 2 || (nextBankAccountStatus == null && bslBalances.length > 0)) {
        sections.push({
          id: "bsl",
          title: "BSL BANK",
          subtitle: "Bank account balances",
          status: "active",
          balances: bslBalances,
        });
      }

      setBalanceSections(sections);
      try {
        sessionStorage.setItem(
          EXCHANGE_BALANCES_STORAGE_KEY,
          JSON.stringify(extractExchangeBalancesFromSections(sections)),
        );
      } catch (_) {
        // no-op
      }
      dispatch(
        setTableData({
          key: "accountSummary",
          data: sections,
          pagination: null,
        }),
      );
      dispatch(
        setFeatureData({
          section: "summary",
          key: "accountSummary",
          value: {
            bankAccountStatus: nextBankAccountStatus,
            dateFrom: from,
            dateTo: to,
          },
        }),
      );
      return sections;
    } finally {
      setBalancesLoading(false);
    }
  }, [dispatch]);

  return {
    getOverviewTransactions,
    getOverviewBalances,
    transactions,
    balanceSections,
    bankAccountStatus,
    totalCount,
    loading,
    balancesLoading,
    error,
  };
}
